import { z } from "zod";
import { PRESET_CAPABILITIES } from "./capabilities.js";
import { LFO_SHAPES, MOD_DESTINATIONS, MOD_SOURCES, PARAM_NAMES, PRESET_STYLES, SEQUENCE_DESTINATIONS, TEMPO_DIVISIONS, WAVETABLE_GENERATORS, } from "./params.js";
const SafeName = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[\w .,'\-()+]+$/u, "unsafe preset name");
const SafeText = z.string().trim().max(500);
const WavetableGenerator = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("basic"),
        shape: z.enum(WAVETABLE_GENERATORS),
    })
        .strict(),
    z
        .object({
        kind: z.literal("harmonic"),
        harmonics: z.array(z.number().finite().min(0).max(1)).min(1).max(64),
    })
        .strict(),
    z
        .object({
        kind: z.literal("pulse"),
        width: z.number().finite().min(0.05).max(0.95),
    })
        .strict(),
]);
const ModulatorPoint = z
    .object({
    x: z.number().finite().min(0).max(1),
    y: z.number().finite().min(0).max(1),
    power: z.number().finite().min(-10).max(10).optional(),
})
    .strict();
export const ModulatorShape = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("preset"),
        shape: z.enum(LFO_SHAPES),
    })
        .strict(),
    z
        .object({
        kind: z.literal("custom"),
        points: z
            .array(ModulatorPoint)
            .min(PRESET_CAPABILITIES.limits.minCustomPoints)
            .max(PRESET_CAPABILITIES.limits.maxCustomPoints),
        smooth: z.boolean(),
    })
        .strict(),
    z
        .object({
        kind: z.literal("steps"),
        values: z
            .array(z.number().finite().min(0).max(1))
            .min(1)
            .max(PRESET_CAPABILITIES.limits.maxStepValues),
        smoothing: z.number().finite().min(0).max(1).optional(),
    })
        .strict(),
]);
export const ModulatorTiming = z.discriminatedUnion("mode", [
    z
        .object({
        mode: z.literal("free_hz"),
        frequencyHz: z.number().finite().min(0.01).max(20),
    })
        .strict(),
    z
        .object({
        mode: z.literal("tempo"),
        division: z.enum(TEMPO_DIVISIONS),
    })
        .strict(),
]);
export const ModulatorTrigger = z.enum(["note_retrigger", "sync", "free", "one_shot"]);
const SequenceStep = z
    .object({
    semitones: z.number().finite().min(-24).max(24),
    gate: z.number().finite().min(0).max(1),
    accent: z.number().finite().min(0).max(1).optional(),
})
    .strict();
const SequenceLane = z
    .object({
    destination: z.enum(SEQUENCE_DESTINATIONS),
    values: z
        .array(z.number().finite().min(0).max(1))
        .min(1)
        .max(PRESET_CAPABILITIES.limits.maxSequenceSteps),
    amount: z.number().finite().min(-1).max(1),
    bipolar: z.boolean().optional(),
    smoothing: z.number().finite().min(0).max(1).optional(),
})
    .strict();
const MacroAssignment = z
    .object({
    destination: z.enum(MOD_DESTINATIONS),
    amount: z.number().finite().min(-1).max(1),
    bipolar: z.boolean().optional(),
})
    .strict();
export const PatchOp = z.discriminatedUnion("op", [
    z
        .object({
        op: z.literal("set"),
        param: z.enum(PARAM_NAMES),
        value: z.number().finite(),
    })
        .strict(),
    z
        .object({
        op: z.literal("modulate"),
        slot: z.number().int().min(1).max(64),
        source: z.enum(MOD_SOURCES),
        destination: z.enum(MOD_DESTINATIONS),
        amount: z.number().min(-1).max(1),
        bipolar: z.boolean().optional(),
    })
        .strict(),
    z
        .object({
        op: z.literal("unmodulate"),
        slot: z.number().int().min(1).max(64),
    })
        .strict(),
    z
        .object({
        op: z.literal("lfo_shape"),
        index: z.number().int().min(1).max(8),
        shape: z.enum(LFO_SHAPES),
    })
        .strict(),
    z
        .object({
        op: z.literal("set_modulator"),
        index: z.number().int().min(1).max(8),
        shape: ModulatorShape,
        timing: ModulatorTiming,
        trigger: ModulatorTrigger,
        phase: z.number().finite().min(0).max(1).optional(),
        fadeSeconds: z.number().finite().min(0).max(8).optional(),
        delaySeconds: z.number().finite().min(0).max(4).optional(),
        stereoOffset: z.number().finite().min(-0.5).max(0.5).optional(),
    })
        .strict(),
    z
        .object({
        op: z.literal("sequence"),
        index: z.number().int().min(1).max(4),
        division: z.enum(TEMPO_DIVISIONS),
        steps: z
            .array(SequenceStep)
            .min(1)
            .max(PRESET_CAPABILITIES.limits.maxSequenceSteps),
        lanes: z.array(SequenceLane).max(PRESET_CAPABILITIES.limits.maxSequenceLanes).optional(),
        retrigger: z.enum(["note", "sync", "free"]),
        swing: z.number().finite().min(0).max(1).optional(),
    })
        .strict(),
    z
        .object({
        op: z.literal("set_macro"),
        index: z.number().int().min(1).max(4),
        name: SafeName,
        assignments: z.array(MacroAssignment).max(8),
    })
        .strict(),
    z
        .object({
        op: z.literal("wavetable"),
        osc: z.number().int().min(1).max(3),
        generator: WavetableGenerator,
    })
        .strict(),
    z
        .object({
        op: z.literal("meta"),
        presetName: SafeName,
        style: z.enum(PRESET_STYLES),
        comments: SafeText.optional(),
    })
        .strict(),
]);
export const MAX_PATCH_OPS = PRESET_CAPABILITIES.limits.maxOps;
export const PatchOpList = z.array(PatchOp).max(MAX_PATCH_OPS);
/** JSON Schema fragment for provider structured output (Zod → plain object). */
export function patchOpJsonSchema() {
    return {
        $schema: "https://json-schema.org/draft/2020-12/schema",
        title: "DefibrillatorPatchOpList",
        type: "array",
        maxItems: MAX_PATCH_OPS,
        items: {
            oneOf: [
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "param", "value"],
                    properties: {
                        op: { const: "set" },
                        param: { type: "string", enum: [...PARAM_NAMES] },
                        value: { type: "number" },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "slot", "source", "destination", "amount"],
                    properties: {
                        op: { const: "modulate" },
                        slot: { type: "integer", minimum: 1, maximum: 64 },
                        source: { type: "string", enum: [...MOD_SOURCES] },
                        destination: { type: "string", enum: [...MOD_DESTINATIONS] },
                        amount: { type: "number", minimum: -1, maximum: 1 },
                        bipolar: { type: "boolean" },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "slot"],
                    properties: {
                        op: { const: "unmodulate" },
                        slot: { type: "integer", minimum: 1, maximum: 64 },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "index", "shape"],
                    properties: {
                        op: { const: "lfo_shape" },
                        index: { type: "integer", minimum: 1, maximum: 8 },
                        shape: { type: "string", enum: [...LFO_SHAPES] },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "index", "shape", "timing", "trigger"],
                    properties: {
                        op: { const: "set_modulator" },
                        index: { type: "integer", minimum: 1, maximum: 8 },
                        shape: { type: "object" },
                        timing: { type: "object" },
                        trigger: {
                            type: "string",
                            enum: ["note_retrigger", "sync", "free", "one_shot"],
                        },
                        phase: { type: "number", minimum: 0, maximum: 1 },
                        fadeSeconds: { type: "number", minimum: 0, maximum: 8 },
                        delaySeconds: { type: "number", minimum: 0, maximum: 4 },
                        stereoOffset: { type: "number", minimum: -0.5, maximum: 0.5 },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "index", "division", "steps", "retrigger"],
                    properties: {
                        op: { const: "sequence" },
                        index: { type: "integer", minimum: 1, maximum: 4 },
                        division: { type: "string", enum: [...TEMPO_DIVISIONS] },
                        steps: { type: "array", minItems: 1, maxItems: 32 },
                        lanes: { type: "array", maxItems: 4 },
                        retrigger: { type: "string", enum: ["note", "sync", "free"] },
                        swing: { type: "number", minimum: 0, maximum: 1 },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "index", "name", "assignments"],
                    properties: {
                        op: { const: "set_macro" },
                        index: { type: "integer", minimum: 1, maximum: 4 },
                        name: { type: "string", minLength: 1, maxLength: 64 },
                        assignments: { type: "array", maxItems: 8 },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "osc", "generator"],
                    properties: {
                        op: { const: "wavetable" },
                        osc: { type: "integer", minimum: 1, maximum: 3 },
                        generator: { type: "object" },
                    },
                },
                {
                    type: "object",
                    additionalProperties: false,
                    required: ["op", "presetName", "style"],
                    properties: {
                        op: { const: "meta" },
                        presetName: { type: "string", minLength: 1, maxLength: 64 },
                        style: { type: "string", enum: [...PRESET_STYLES] },
                        comments: { type: "string", maxLength: 500 },
                    },
                },
            ],
        },
    };
}
//# sourceMappingURL=patch-op.js.map